// 🤖 AI Event-voorstellen widget — toont mails waar AI een afspraak detecteerde
// Verschijnt bovenaan Mail Hub sidebar, met 1-klik accepteer/negeer per voorstel
import React, { useEffect, useState, useCallback } from 'react';
import API from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Sparkle, CheckCircle, X, MapPin, CaretDown, CaretUp } from '@phosphor-icons/react';

const safeParse = (s) => { try { return parseISO(s); } catch { return new Date(s); } };

export default function MailEventProposals({ onAccepted }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [busyMid, setBusyMid] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await API.get('/agenda/proposals');
      setItems(r.data?.proposals || []);
    } catch { /* silent fail */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh elke 60 sec (in geval IMAP-sync nieuwe voorstellen vond)
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const accept = async (mid) => {
    setBusyMid(mid);
    try {
      await API.post(`/agenda/proposals/${mid}/accept`);
      setItems(prev => prev.filter(p => p.mail_id !== mid));
      if (onAccepted) onAccepted();
    } catch (e) {
      alert('Accepteren mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusyMid(null);
  };

  const dismiss = async (mid) => {
    setBusyMid(mid);
    try {
      await API.post(`/agenda/proposals/${mid}/dismiss`);
      setItems(prev => prev.filter(p => p.mail_id !== mid));
    } catch (e) {
      alert('Negeren mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusyMid(null);
  };

  if (items.length === 0) return null;

  return (
    <div className="mb-3 bg-gradient-to-br from-violet-50 to-indigo-50 border-2 border-violet-300 rounded-lg overflow-hidden" data-testid="mail-event-proposals">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-violet-100 transition"
        data-testid="mail-event-proposals-toggle"
      >
        <span className="flex items-center gap-1 text-[11px] font-bold text-violet-800">
          <Sparkle size={12} weight="fill" />
          AI Afspraken
          <span className="bg-violet-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{items.length}</span>
        </span>
        {open ? <CaretUp size={11} className="text-violet-600" /> : <CaretDown size={11} className="text-violet-600" />}
      </button>
      {open && (
        <div className="px-1.5 pb-1.5 space-y-1 max-h-64 overflow-y-auto">
          {items.map(p => {
            const startDate = p.start ? safeParse(p.start) : null;
            return (
              <div
                key={p.mail_id}
                className="bg-white rounded border border-violet-200 p-1.5 text-[10px] shadow-sm"
                data-testid={`mail-event-proposal-${p.mail_id}`}
              >
                <div className="font-semibold text-gray-800 truncate" title={p.titel}>{p.titel || p.mail_subject}</div>
                {startDate && (
                  <div className="text-violet-700 mt-0.5">
                    📅 {format(startDate, 'd MMM HH:mm', { locale: nl })}
                    {p.eind && ` → ${format(safeParse(p.eind), 'HH:mm')}`}
                  </div>
                )}
                {p.locatie && (
                  <div className="text-gray-500 truncate flex items-center gap-0.5 mt-0.5" title={p.locatie}>
                    <MapPin size={9} /> {p.locatie}
                  </div>
                )}
                <div className="text-gray-400 truncate mt-0.5" title={p.mail_from}>
                  ✉ {p.mail_from}
                </div>
                <div className="flex gap-1 mt-1.5">
                  <button
                    onClick={() => accept(p.mail_id)}
                    disabled={busyMid === p.mail_id}
                    className="flex-1 bg-violet-600 hover:bg-violet-700 text-white text-[10px] font-semibold py-1 rounded inline-flex items-center justify-center gap-0.5 disabled:opacity-50"
                    title={`Confidence ${Math.round((p.confidence || 0) * 100)}% • ${p.redenering || ''}`}
                    data-testid={`mail-event-proposal-accept-${p.mail_id}`}
                  >
                    <CheckCircle size={10} weight="bold" /> Accepteer
                  </button>
                  <button
                    onClick={() => dismiss(p.mail_id)}
                    disabled={busyMid === p.mail_id}
                    className="px-1.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded inline-flex items-center justify-center disabled:opacity-50"
                    title="Negeren"
                    data-testid={`mail-event-proposal-dismiss-${p.mail_id}`}
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
